/**
 * Auth API sarmalayicilari.
 *
 * ## `next`i SUNUCU soyluyor, istemci TAHMIN ETMIYOR
 *
 * Her giris/dogrulama cagrisi ayristirici bir `next` donduruyor:
 * `session` | `totp` | `verify_email`. Istemci "TOTP acik mi acaba" gibi bir
 * karar VERMIYOR - o karar sunucunun kullanici satirinda ve tek dogruluk
 * kaynagi orasi. Istemcide yeniden turetmek, iki yerin ayri ayri
 * degismesiyle sessizce bozulan bir tekrar olurdu (bu kod tabaninda tam
 * olarak boyle iki tekrar zaten silindi).
 *
 * ## Enumeration yok
 *
 * `register` ve `password/forgot` e-posta VAR OLSUN OLMASIN ayni yaniti ve
 * ayni Turkce metni donduruyor. Arayuz de bu yuzden ikisini ayirt eden bir
 * mesaj GOSTERMIYOR.
 */

import { apiJson, apiVoid } from './api'

export type NextStep = 'session' | 'totp' | 'verify_email'

export interface AuthUser {
  id: string
  email: string
  name: string
  role: string
  status: string
  totp_enabled: boolean
  can_run_code: boolean
}

export interface LoginResult {
  next: NextStep
  user?: AuthUser
  methods?: string[]
  /** Kodun gecerlilik suresi (saniye) - geri sayim icin. */
  code_ttl_seconds?: number
}

export interface CodeResult {
  code_ttl_seconds?: number
}

export function login(email: string, password: string): Promise<LoginResult> {
  return apiJson<LoginResult>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  })
}

export function register(input: {
  email: string
  name: string
  password: string
}): Promise<CodeResult> {
  return apiJson<CodeResult>('/api/auth/register', {
    method: 'POST',
    // `website` bot tuzagi: gercek kullanici gormez ve doldurmaz. Bos
    // gonderiyoruz; alan sunucuda var oldugu icin acikca bos gecmek,
    // sunucunun varsayilanina guvenmekten iyi.
    body: JSON.stringify({ ...input, website: '' }),
  })
}

export function verifyEmail(email: string, code: string): Promise<{ user: AuthUser }> {
  return apiJson<{ user: AuthUser }>('/api/auth/verify-email', {
    method: 'POST',
    body: JSON.stringify({ email, code }),
  })
}

export function resendCode(email: string, purpose: 'activate' | 'reset'): Promise<CodeResult> {
  return apiJson<CodeResult>('/api/auth/resend-code', {
    method: 'POST',
    body: JSON.stringify({ email, purpose }),
  })
}

export function loginTotp(code: string): Promise<LoginResult> {
  return apiJson<LoginResult>('/api/auth/login/totp', {
    method: 'POST',
    body: JSON.stringify({ code }),
  })
}

export function loginBackupCode(code: string): Promise<LoginResult> {
  return apiJson<LoginResult>('/api/auth/login/backup-code', {
    method: 'POST',
    body: JSON.stringify({ code }),
  })
}

export function forgotPassword(email: string): Promise<CodeResult> {
  return apiJson<CodeResult>('/api/auth/password/forgot', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })
}

export function resetPassword(input: {
  email: string
  code: string
  new_password: string
}): Promise<void> {
  return apiVoid('/api/auth/password/reset', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function logout(): Promise<void> {
  return apiVoid('/api/auth/logout', { method: 'POST' })
}

// --------------------------------------------------------------------- TOTP

export interface EnrollResult {
  secret: string
  otpauth_uri: string
  digits: number
  period: number
}

export function totpEnroll(password: string): Promise<EnrollResult> {
  return apiJson<EnrollResult>('/api/me/totp/enroll', {
    method: 'POST',
    body: JSON.stringify({ password }),
  })
}

export function totpConfirm(code: string): Promise<{ backup_codes: string[] }> {
  return apiJson<{ backup_codes: string[] }>('/api/me/totp/confirm', {
    method: 'POST',
    body: JSON.stringify({ code }),
  })
}

/**
 * TOTP kapatma - IKI ADIM.
 *
 * `request`: sifre + authenticator kodu dogrulanir, mevcut e-postaya bir
 * OTP gonderilir. `confirm`: o OTP dogrulanmadan TOTP fiilen KAPANMAZ -
 * tek adimda kapatmak, oturumu ele gecirmis birinin ikinci faktoru TEK
 * basina sokup atabilmesi demek olurdu.
 */
export function totpDisableRequest(
  password: string,
  code: string,
): Promise<{ status: string; code_sent_to: string }> {
  return apiJson<{ status: string; code_sent_to: string }>('/api/me/totp/disable/request', {
    method: 'POST',
    body: JSON.stringify({ password, code }),
  })
}

export function totpDisableConfirm(code: string): Promise<void> {
  return apiVoid('/api/me/totp/disable/confirm', {
    method: 'POST',
    body: JSON.stringify({ code }),
  })
}

// ------------------------------------------------------------------- Google

/**
 * Google giris baslangici.
 *
 * `surface=app` sunucuya donuste `/app/`e gitmesini soyluyor. Deger sunucuda
 * bir ALLOWLIST'ten geciyor ve IMZALI state'in icinde tasiniyor - serbest bir
 * donus adresi acik yonlendirme acigi olurdu.
 */
export const GOOGLE_START_URL = '/api/auth/google/start?surface=app'
